import { GET_VERSION_RECORDS } from '@/api';
import { caseApplicantService } from '@/api/caseApplicant';
import { complaintService } from '@/api/complaint';
import { PDFComplaint, PDFDemandLetter } from '@/components/screens';
import { Icon, Spinner, Typography } from '@/components/common';
import SimpleTable from '@/components/common/tables/simpleTable';
import { CaseContext } from '@/context/CaseContext';
import { useNotification } from '@/hooks';
import { CaseType, ComplaintDocument, VersionRecordData } from '@/interfaces';
import { pdf } from '@react-pdf/renderer';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import * as React from 'react';
import Icons from '@/const/icons';

export const GeneratedFilesHistory: React.FC = () => {
	const router = useRouter();
	const caseId = router.query.id as string;
	const { toastError } = useNotification();
	const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

	const { caseDetails } = React.useContext(CaseContext);

	const { data: versionRecords, isLoading } = useQuery({
		queryKey: [GET_VERSION_RECORDS, caseId],
		queryFn: () => caseApplicantService.getVersionRecords(caseId || ''),
		enabled: !!caseId,
		refetchOnWindowFocus: false,
	});

	const downloadPDF = async (id: string, caseType: CaseType) => {
		setDownloadingId(id);
		try {
			const data = await complaintService.getComplaintVersion(id);
			const pdfFile = await generatePDF(data.data, caseType);

			if (!pdfFile) return;

			const url = URL.createObjectURL(pdfFile);
			const a = document.createElement('a');
			a.href = url;
			a.download = `${
				caseType === 'CLASS_ACTION' ? 'Complaint' : 'Demand Letter'
			}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		} catch (error) {
			toastError(
				'An error occurred while downloading the PDF. Please try again.'
			);
		} finally {
			setDownloadingId(null);
		}
	};

	const generatePDF = async (
		documentInfo: ComplaintDocument[],
		caseType: CaseType
	) => {
		try {
			const blob = await pdf(
				caseType === 'MASS_ACTION' ? (
					<PDFDemandLetter
						tableData={documentInfo[0]}
						caseName={caseDetails?.title}
					/>
				) : (
					<PDFComplaint
						documentInfo={documentInfo[0]}
						caseName={caseDetails?.title}
					/>
				)
			).toBlob();
			return blob;
		} catch (error) {
			console.error('Error downloading PDF:', error);
		}
	};

	return (
		<div>
			<div className="flex items-center justify-between w-full py-2 px-4 border border-grey-100 rounded-t-2xl bg-gray-15">
				<Typography type="regular-14" className="text-black">
					Generated Files History
				</Typography>
				<Typography type="regular-14" className="text-opacity-black">
					Total Files: {versionRecords?.data.length}
				</Typography>
			</div>
			<SimpleTable<
				VersionRecordData & { isActive: boolean; isEmptyRow?: boolean }
			>
				hideRowActions
				classNameContainer="!rounded-t-none !rounded-b-2xl shadow-none !pr-0 max-h-[400px] overflow-y-auto"
				classNameHeader="grid grid-cols-[1.5fr,1fr,1fr,1fr,1fr,0.5fr] !bg-gray-15"
				classNameRows="grid grid-cols-[1.5fr,1fr,1fr,1fr,1fr,0.5fr] hover:!bg-transparent hover:!border-b-dark-10 hover:!border-t-dark-10 hover:!border-x-transparent !rounded-none"
				classNameHead="!opacity-100"
				classNameHeaderItem="!px-2"
				classNameColumn="!px-2"
				isLoading={isLoading}
				disableHoverEffects
				columns={[
					{
						header: 'File Name',
						content: (instance) =>
							instance.isEmptyRow ? null : (
								<span className="text-xs text-black">{instance.name}</span>
							),
					},
					{
						header: 'Total Clients',
						content: (instance) =>
							instance.isEmptyRow ? null : (
								<span className="bg-grey-100 py-0.5 px-1.5 rounded-md text-xs">
									{instance.total_users}{' '}
									{instance.total_users === 1 ? 'Client' : 'Clients'}
								</span>
							),
					},
					{
						header: 'Generated',
						content: (instance) =>
							instance.isEmptyRow ? null : instance.created_at ? (
								<span className="text-xs">
									{format(parseISO(instance.created_at), 'MMM dd, yyyy')}
									<br />
									<span className="text-grey-40">
										{format(parseISO(instance.created_at), 'HH:mm a')}
									</span>
								</span>
							) : (
								<></>
							),
					},
					{
						header: 'Type',
						content: (instance) =>
							instance.isEmptyRow ? null : (
								<span className="text-xs">All Users</span>
							),
					},
					{
						header: 'Case Type',
						content: (instance) =>
							instance.isEmptyRow ? null : (
								<span className="text-xs">
									{instance.case_type === 'CLASS_ACTION'
										? 'Class Action'
										: 'Mass Action'}
								</span>
							),
					},
					{
						header: 'Action',
						content: (instance) =>
							instance.isEmptyRow ? null : (
								<>
									<button
										type="button"
										onClick={() => downloadPDF(instance.id, instance.case_type)}
									>
										{downloadingId === instance.id ? (
											<Spinner />
										) : (
											<Icon
												src={Icons.documentDownloadThin}
												width={24}
												height={24}
											/>
										)}
									</button>
								</>
							),
					},
				]}
				rows={
					versionRecords && versionRecords?.data.length > 0
						? versionRecords?.data.map((item) => ({
								...item,
								isActive: false,
						  }))
						: [
								{
									id: '',
									case_id: '',
									name: '',
									total_users: 0,
									created_at: '',
									case_type: 'CLASS_ACTION',
									isActive: false,
									isEmptyRow: true,
								},
						  ]
				}
				hasPagination={false}
			/>
		</div>
	);
};
